import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Camera } from 'lucide-react';
import ProfileImageUploadModal from './ProfileImageUploadModal';

interface ArtistAboutProps {
  profileImageUrl: string | null;
  slug: string;
  onProfileImageUpdate?: (newUrl: string) => void;
}

const DEFAULT_PROFILE_IMAGE = '/fans-audio-default-profile.png';

/**
 * ArtistAbout — Profile Image section.
 * Shows the artist's profile image with a hover overlay to change it.
 */
export default function ArtistAbout({
  profileImageUrl,
  slug,
  onProfileImageUpdate,
}: ArtistAboutProps) {
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(profileImageUrl);

  const displayImage = currentImageUrl || DEFAULT_PROFILE_IMAGE;

  const handleImageClick = () => {
    setShowUploadModal(true);
  };

  const handleUploadComplete = useCallback((newUrl: string) => {
    setCurrentImageUrl(newUrl);
    onProfileImageUpdate?.(newUrl);
  }, [onProfileImageUpdate]);

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-white">Profile Image</h2>
      </div>

      {/* Profile Image */}
      <div className="relative rounded-xl overflow-hidden bg-matte-black border border-white/5">
        <div className="relative w-full overflow-hidden aspect-square">
          {/* Clickable image region */}
          <div
            className="absolute inset-0 cursor-pointer"
            onMouseEnter={() => setIsImageHovered(true)}
            onMouseLeave={() => setIsImageHovered(false)}
            onClick={handleImageClick}
          >
            <img
              src={displayImage}
              alt="Artist profile"
              className="w-full h-full object-cover"
            />

            {/* Edit Profile Image overlay on hover */}
            <motion.div
              initial={false}
              animate={{ opacity: isImageHovered ? 1 : 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 flex items-center justify-center bg-black/50"
              style={{ pointerEvents: isImageHovered ? 'auto' : 'none' }}
            >
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-black/60 backdrop-blur-md border border-white/20 text-white text-sm font-medium">
                <Camera className="w-4 h-4" />
                <span>Edit Profile Image</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Profile Image Upload Modal */}
      <ProfileImageUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        slug={slug}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
}
